import { Box, Button, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { FundFamilyStateBadge } from "features/fund-family/FundFamilyStateBadge";
import { useCreditFacilityListSuspense } from "gen/endpoints/credit-facility/credit-facility";
import {
  getFundFamilyRetrieveQueryKey,
  useFundFamilyRetrieveSuspense,
} from "gen/endpoints/fund-family/fund-family";
import { useFundFamilyFundraisingUpdate } from "gen/endpoints/fundraise/fundraise";
import { FundFamilyStateMachineFieldsEnum } from "gen/model/fundFamilyStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { compact } from "lodash-es";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import PageSidebar from "shared/components/layout/PageSidebar";
import { NavigationItem } from "shared/components/navbar/NavigationItem";
import SidebarNav from "shared/components/navbar/side/SideNavBar";
import { useToast } from "shared/components/toast/useToast";
import { useFeatureFlagsActive } from "shared/hooks/useFeatureFlag";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useHasPermission } from "shared/utils/PermissionUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export interface FundFamilySidebarProps {
  showSidebar: boolean;
}

export const FundFamilySidebar: React.FC<FundFamilySidebarProps> = ({
  showSidebar,
}) => (
  <PageSidebar defaultIsPersistent={showSidebar}>
    <Box width="100%">
      <QueryBoundary>
        <FundFamilySidebarContent />
      </QueryBoundary>
    </Box>
  </PageSidebar>
);

const FundFamilySidebarContent: React.FC = () => {
  const { t } = useTranslation(["common", "fundFamily"]);
  const { fundFamilyId } = useRequiredStringParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data } = useFundFamilyRetrieveSuspense(fundFamilyId);
  const { data: creditFacilityList } =
    useCreditFacilityListSuspense(fundFamilyId);

  const { mutateAsync: moveFundFamilyToFundraising } =
    useFundFamilyFundraisingUpdate();

  const fundFamilyName = data.name;
  const featureFlagValues = useFeatureFlagsActive([
    "fund_family_setup",
    "general_ledger",
  ]);
  const isServiceUser = useHasPermission(PermissionTypeEnum.service);

  const isCreditFacilityConfigured =
    creditFacilityList[0] && creditFacilityList[0].state !== "draft";

  const fundFamilyRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId });

  const isDraft =
    data.state === FundFamilyStateMachineFieldsEnum.draft &&
    featureFlagValues.get("fund_family_setup");

  const navItems = useMemo((): NavigationItem[] => {
    const fundFamilySetupRoute = fundFamilyRoute.setup({});
    if (isDraft) {
      return [
        {
          id: "profile",
          name: "Profile",
          href: fundFamilySetupRoute.profile({}).$,
        },
        {
          id: "dates",
          name: "Dates",
          href: fundFamilySetupRoute.dates({}).$,
        },
        {
          id: "entities",
          name: "Entities",
          href: fundFamilySetupRoute.entities({}).$,
        },
        {
          id: "transactions",
          name: "Transactions",
          href: fundFamilySetupRoute.transactions({}).$,
        },
        {
          id: "calculations",
          name: "Calculations",
          href: fundFamilySetupRoute.calculations({}).$,
        },
        {
          id: "fees",
          name: "Fees",
          href: fundFamilySetupRoute.fees({}).configuration({}).$,
        },
        {
          id: "waterfalls",
          name: "Waterfalls",
          href: fundFamilySetupRoute.waterfalls({}).$,
        },
      ];
    }

    const fundFamilyConfigurationRoute = fundFamilyRoute.configuration({});
    return compact([
      {
        id: "summary",
        name: t("fundFamily:summary.navTitle"),
        href: fundFamilyRoute.summary({}).$,
      },
      {
        id: "commitments",
        name: t("common:commitments"),
        href: fundFamilyRoute.commitments({}).$,
      },
      {
        id: "capital-activity",
        name: t("common:capitalActivity"),
        href: fundFamilyRoute.capitalActivity({}).$,
      },
      {
        id: "fees",
        name: t("common:fees"),
        href: fundFamilyRoute.fees({}).$,
      },
      {
        id: "investments",
        name: t("common:investments"),
        href: fundFamilyRoute.investments({}).$,
      },
      isCreditFacilityConfigured && {
        id: "credit-facilities",
        name: t("common:creditFacility"),
        href: fundFamilyRoute.creditFacilities({}).index({}).$,
      },
      {
        id: "financial-reporting",
        name: t("common:financialReporting"),
        href: fundFamilyRoute.financialReporting({}).index({}).$,
      },
      {
        id: "configuration",
        name: t("common:configuration"),
        href: "",
        items: compact([
          {
            id: "config-lpa-config",
            name: t("common:lpaConfig"),
            href: fundFamilyConfigurationRoute.fund({}).$,
          },
          {
            id: "config-fund-structure",
            name: t("common:fundStructure"),
            href: fundFamilyConfigurationRoute.fundStructure({}).$,
          },
          {
            id: "config-calculations",
            name: t("common:calculations"),
            href: fundFamilyConfigurationRoute.calculations({}).$,
          },
          {
            id: "config-transaction-codes",
            name: t("common:transactionCodes"),
            href: fundFamilyConfigurationRoute.transactionCodes({}).$,
          },
          isServiceUser && {
            id: "config-notices",
            name: t("common:notices"),
            href: fundFamilyConfigurationRoute.notices({}).$,
          },
          {
            id: "config-credit-facilities",
            name: t("common:creditFacility"),
            href: fundFamilyConfigurationRoute.creditFacilities({}).$,
          },
        ]),
      },
    ]);
  }, [fundFamilyRoute, isDraft, t, isCreditFacilityConfigured, isServiceUser]);

  const completeSetup = async () => {
    if (!isDraft) {
      toast.showToast({
        variant: "error",
        message:
          "Looks like you're trying to start a first close on a " +
          "fund that is not in draft mode. Please save your changes and try again.",
      });
    }
    await moveFundFamilyToFundraising({
      fundFamilyId,
    });
    await invalidateQueryKeys(
      queryClient,
      getFundFamilyRetrieveQueryKey(fundFamilyId),
    );
    navigate(fundFamilyRoute.commitments({}).closingsTab({}).$);
  };

  return (
    <>
      <Box
        borderBottomWidth={1}
        borderColor="gray.200"
        px="24px"
        py="16px"
        mb="8px"
        bg="white"
      >
        <Text textStyle="h5">{fundFamilyName}</Text>
        <Text textStyle="small" color="gray.500" mt="18px">
          {t("fundFamily:fundPhase")}
        </Text>
        {data.state && <FundFamilyStateBadge state={data.state} mt="4px" />}
      </Box>
      <SidebarNav items={navItems} isDraft={isDraft} />
      {isDraft && (
        <Box width="100%" mt="16px" padding="6px 12px 6px 12px">
          <Box
            backgroundColor="primary.100"
            padding="6px 12px 6px 12px"
            rounded="md"
            display="flex"
            flexDirection="column"
          >
            <Text textStyle="bold" mt="4px">
              First Closing
            </Text>
            <Text textStyle="small" color="gray.500" mt="4px">
              You can complete the setup process by clicking the button below.
            </Text>
            <Button
              colorScheme="primary"
              mt="16px"
              mb="8px"
              size="xs"
              onClick={completeSetup}
            >
              Start First Close
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};
